import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { DeviceType } from '@/types/deviceType';

export interface InterfaceRelationshipInfo {
  interfaceId: string;
  interfaceName: string;
  relationshipType: string; // "lag", "stack"
  status: string;
}

export interface DeviceInterface {
  id: string;
  interfaceName: string;
  interfaceType: string | null;
  macAddress: string | null;
  adminStatus: string | null;
  operStatus: string | null;
  speedMbps: number | null;
  description: string | null;
  ifindex: string | null;
  hardwareMaxSpeedMbps: number | null;
  maxSpeedSeenMbps: number | null;
  securityMode: string | null;
  /** Port protection; absent or null is unread (docs/UplinkLossAndLoopProtection.md). */
  errDisabled?: boolean | null;
  errDisabledCause?: string | null;
  errDisabledSince?: string | null;
  loopShutdown?: boolean | null;
  parentRelationships?: InterfaceRelationshipInfo[];
  childRelationships?: InterfaceRelationshipInfo[];
}

/** One place a device is attached, inferred from FDB (cross-gateway): a switch
 *  port (or AP) that learned one of the device's own MACs. */
/** One thing a device is connected to: a stored link, or the switch port that learned one of its
 *  own MACs. Mirrors website-backend `views::devices::DeviceFdbLocationResponse`. */
export interface DeviceFdbLocation {
  deviceId: string;
  deviceName: string | null;
  interfaceId: string | null;
  interfaceName: string | null;
  /** The device's own interface on that link, or whose MAC that port learned. */
  viaInterfaceId: string | null;
  viaInterfaceName: string | null;
  /** A link's type (`lldp`, …), or how a forwarding table's port was chosen (`fdb_edge` is
   *  assumed). */
  method: string | null;
  since: string | null;
  /** Set when this is a stored link. */
  linkId?: string | null;
  /** Set when the device is only seen through this neighbour: the port learned its MAC through
   *  it. */
  behindDeviceId?: string | null;
  behindDeviceName?: string | null;
}

export interface DeviceDetails {
  id: string;
  name: string | null;
  description: string | null;
  status: string;
  ipAddress: string | null;
  deviceType: DeviceType | null;
  regionId: string;
  regionName: string;
  divisionId: string | null;
  divisionName: string | null;
  organizationId: string;
  /** Gateway currently assigned to poll this device (null = unassigned). */
  assignedGatewayId: string | null;
  /** Resolved name of the assigned gateway (detail endpoint only). */
  assignedGatewayName?: string | null;
  /** Assigned gateway liveness: 'online' | 'offline' | 'unknown'. */
  assignedGatewayStatus?: string | null;
  snmpCredentialId: string | null;
  /**
   * Polling is held off for this device — the gateway is not collecting from
   * it. Reflects whether the hold is *live*: a timed pause whose deadline has
   * passed reads false before the backend sweep clears the row.
   */
  pollingPaused?: boolean;
  /** When a timed pause lapses. Absent for an indefinite pause. */
  pollingPausedUntil?: string;
  createdAt: string;
  updatedAt: string;
  netconfEnabled: boolean;
  defaultPortSecurityMode: string | null;
  hostname: string | null;
  vendor: string | null;
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  softwareVersion: string | null;
  firmwareVersion: string | null;
  chassisMac: string | null;
  sysObjectId: string | null;
  interfaces?: DeviceInterface[];
  /** One per switch port (or AP); absent for switches and unlocated devices. */
  locatedViaFdb?: DeviceFdbLocation[];
  /** NETCONF/REST-API support + setup state (only present when the device's
   *  vendor profile supports at least one). Drives the protocol callouts. */
  protocolSetup?: ProtocolSetup | null;
  /** Whether the device can actually be monitored right now (gateway online +
   *  credentials deployed). Detail endpoint only. Drives the readiness banner. */
  readiness?: DeviceReadiness | null;
  /** Present when this device row IS a gateway (`devices.id == gateways.id`).
   *  The authoritative signal — resolved server-side against the gateways
   *  table, not the mutable `type` string. Detail endpoint only. */
  gateway?: DeviceGatewayInfo | null;
}

/** Mirrors website-backend `views::devices::DeviceGatewayInfo`. */
export interface DeviceGatewayInfo {
  /** Heartbeat-driven liveness: 'online' | 'offline'. */
  status: string;
  /** Last heartbeat (RFC 3339) — the gateway's equivalent of "last polled". */
  lastSeen: string | null;
  /** Software version the gateway reported it is running. */
  softwareVersion: string | null;
  /** CPU architecture the gateway reported (e.g. 'amd64'). */
  architecture: string | null;
}

/** Mirrors website-backend `views::devices::DeviceReadiness`. */
export interface DeviceReadiness {
  /** 'online' | 'offline' | 'none' */
  gateway: string;
  /** 'ok' | 'recoverable' | 'undeployed' | 'none' */
  credentials: string;
  /**
   * Whether anything will actually poll this device: 'ok' | 'none'. 'none' is
   * the web-managed-switch case — an HTTP plane that activated while SNMP never
   * came up, which every other axis reads as healthy.
   */
  polling: string;
  monitored: boolean;
  message?: string | null;
}

/** Per-device NETCONF / REST-API capability + setup state from the vendor profile. */
export interface ProtocolSetup {
  supportsNetconf: boolean;
  netconfConfigured: boolean;
  supportsRestApi: boolean;
  restApiConfigured: boolean;
  /** A vendor profile resolved — i.e. we know what this device is. The honest
   *  answer to "have we identified it", and distinct from every `supports*`
   *  flag: a device can be fully identified and fully supported while
   *  declaring none of them (an EdgeRouter's profile is `cli:`-only). */
  profileMatched: boolean;
  /** Profile declares a `cgi:` block — we know how to *drive* this device's
   *  web UI for config management. */
  supportsWebUi: boolean;
  /** Worth offering to *browse* this device's web UI: it has an http(s)
   *  connection method and no profile has said the family serves no UI.
   *  Much weaker than `supportsWebUi`, and default-on — an unidentified
   *  device has no profile, and those are exactly the ones someone needs
   *  to open in order to help us support them. */
  webUiBrowsable: boolean;
  /** The enable flow reuses the device's SSH credential, so "Set up" is only
   *  actionable when this is true. */
  hasSshCredential: boolean;
}

interface UseDeviceDetailsOptions {
  deviceId: string | null;
  includeInterfaces?: boolean;
}

/**
 * One device's full detail record.
 *
 * ## The key namespace, and why it is singular
 *
 * This query lives under `['device', id, ...]` while every device *list* lives
 * under `['devices', ...]`. That is deliberate — a detail response and a list
 * entry are different shapes, and React Query caches by key, so sharing a prefix
 * across differently-shaped queries lets one poison the other's cache.
 *
 * The cost is that `invalidateQueries` matches by key PREFIX, so
 * `['devices']` does not touch this query. Anything invalidating device data has
 * to name both roots. `useWebSocketCacheSync`'s device handlers do; forgetting it
 * there is what left this page's status chip stale after a live status flip while
 * every list in the product updated correctly.
 */
export function useDeviceDetails({ deviceId, includeInterfaces = false }: UseDeviceDetailsOptions) {
  return useQuery<DeviceDetails>({
    queryKey: ['device', deviceId, includeInterfaces],
    queryFn: async () => {
      if (!deviceId) {
        throw new Error('Device ID is required');
      }

      const params = new URLSearchParams();
      if (includeInterfaces) {
        params.append('include', 'interfaces');
      }

      const url = `/api/devices/${deviceId}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await axios.get<DeviceDetails>(url);
      return response.data;
    },
    enabled: !!deviceId,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
}

export interface UpdateDeviceRequest {
  name?: string | null;
  description?: string | null;
  regionId?: string;
  divisionId?: string | null;
  ipAddr?: string;
  netconfEnabled?: boolean;
}

/**
 * Enable NETCONF or the vendor REST API on an already-managed device. Sends no
 * session_id, so the gateway runs the enable over the device's existing SSH
 * connection (managed-device mode) and records the connection method. The
 * endpoint returns 200 with `{ success, error }`, so we surface a failed enable
 * as a rejected mutation.
 */
export function useEnableProtocol(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (which: 'netconf' | 'rest-api') => {
      const response = await axios.post<{ success: boolean; error?: string }>(
        `/api/devices/${deviceId}/provisioning/enable-${which}`,
        {},
        { withCredentials: true },
      );
      if (!response.data.success) {
        throw new Error(response.data.error || `Failed to enable ${which}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

export function useUpdateDevice(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation<DeviceDetails, Error, UpdateDeviceRequest>({
    mutationFn: async (data) => {
      const response = await axios.put<DeviceDetails>(
        `/api/devices/${deviceId}`,
        data,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
    },
  });
}

/**
 * Hold off / resume the gateway's polling of one device.
 *
 * For firmware that allows a single administrative session, where the
 * gateway's polling collides with an operator working in the device's web UI —
 * Netgear Insight locks the admin account after a few failed logins, and that
 * lockout is account-wide, so it takes out the operator too.
 *
 * Pausing de-assigns the device, which stops every collector. The Web UI tab
 * and config operations keep working (they resolve a gateway by region, not by
 * assignment), and the device stays counted as monitored.
 */
export function usePausePolling(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation<DeviceDetails, Error, { minutes?: number }>({
    mutationFn: async ({ minutes }) => {
      const response = await axios.post<DeviceDetails>(
        `/api/devices/${deviceId}/polling/pause`,
        { minutes: minutes ?? null },
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

export function useResumePolling(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation<DeviceDetails>({
    mutationFn: async () => {
      const response = await axios.post<DeviceDetails>(
        `/api/devices/${deviceId}/polling/resume`,
        {},
        { withCredentials: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device', deviceId] });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}
